import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import api from '../../service/api';
import '../../css/Coronariano.css';
import '../../css/style.css';
import { PieChart, Pie, Cell } from 'recharts';
import { HelpCircle } from "lucide-react";

const PERGUNTAS_CORONARIANO = [
  { id: 0, texto: "1. Idade (anos)" },
  { id: 1, texto: "2. Hereditariedade" },
  { id: 2, texto: "3. Peso" },
  { id: 3, texto: "4. Fumo" },
  { id: 4, texto: "5. Pressão arterial" },
  { id: 5, texto: "6. Colesterol ou % de gordura na dieta" },
  { id: 6, texto: "7. Exercícios" },
  { id: 7, texto: "8. Sexo" }
];

const OPCOES_CORONARIANO = [
  { id: 0, opcoes: [{ label: '10 a 20', peso: 1 }, { label: '21 a 30', peso: 2 }, { label: '31 a 40', peso: 3 }, { label: '41 a 50', peso: 4 }, { label: '51 a 60', peso: 6 }, { label: '61 a 70', peso: 8 }] },
  { id: 1, opcoes: [{ label: 'Nenhuma história reconhecida', peso: 1 }, { label: '1 parente com mais de 60 anos com doença cardiovascular', peso: 2 }, { label: '2 parentes com mais de 60 anos com doença cardiovascular', peso: 3 }, { label: '1 parente com menos de 60 anos com doença cardiovascular', peso: 4 }, { label: '2 parentes com menos de 60 anos com doença cardiovascular', peso: 6 }, { label: '3 parentes com menos de 60 anos com doença cardiovascular', peso: 8 }] },
  { id: 2, opcoes: [{ label: 'Menos de 2,3kg abaixo do peso padrão', peso: 0 }, { label: 'De 2,4kg a 2,9kg acima', peso: 1 }, { label: 'De 3kg a 8,9kg acima', peso: 2 }, { label: 'De 9kg a 14,9kg acima', peso: 3 }, { label: 'De 15kg a 21,9kg acima', peso: 5 }, { label: 'Acima de 22kg', peso: 7 }] },
  { id: 3, opcoes: [{ label: 'Não fumante', peso: 0 }, { label: 'Charuto e/ou cachimbo', peso: 1 }, { label: '10 cigarros ou menos por dia', peso: 2 }, { label: '20 cigarros por dia', peso: 4 }, { label: '30 cigarros por dia', peso: 6 }, { label: '40 cigarros ou mais por dia', peso: 10 }] },
  { id: 4, opcoes: [{ label: 'Limite superior de 100', peso: 1 }, { label: 'Limite superior de 120', peso: 2 }, { label: 'Limite superior de 140', peso: 3 }, { label: 'Limite superior de 160', peso: 4 }, { label: 'Limite superior de 180', peso: 6 }, { label: 'Limite superior de 200', peso: 8 }] },
  { id: 5, opcoes: [{ label: 'Colesterol abaixo de 180mg% / Ausência de gorduras animais ou sólidas na dieta', peso: 1 }, { label: 'Colesterol de 181-205 mg% / 10% de gordura animal ou sólida na dieta', peso: 2 }, { label: 'Colesterol de 203-206 mg% / 20% de gordura animal ou sólida na dieta', peso: 3 }, { label: 'Colesterol de 231-255 mg% / 30% de gordura animal ou sólida na dieta', peso: 4 }, { label: 'Colesterol de 256-280 mg% / 40% de gordura animal ou sólida na dieta', peso: 5 }, { label: 'Colesterol de 281-330 mg% / 50% de gordura animal ou sólida na dieta', peso: 7 }] },
  { id: 6, opcoes: [{ label: 'Esforço Ocupacional e recreacional intenso', peso: 1 }, { label: 'Esforço Ocupacional e recreacional moderado', peso: 2 }, { label: 'Trabalho sedentário e esforço recreacional intenso', peso: 3 }, { label: 'Trabalho sedentário e esforço recreacional moderado', peso: 4 }, { label: 'Trabalho sedentário e esforço recreacional leve', peso: 5 }, { label: 'Ausência completa de qualquer exercício', peso: 7 }] },
  { id: 7, opcoes: [{ label: 'Mulher com menos de 40', peso: 1 }, { label: 'Mulher com 40 a 50', peso: 2 }, { label: 'Mulher com mais de 50', peso: 3 }, { label: 'Homem', peso: 5 }, { label: 'Homem endomesomórfico', peso: 6 }, { label: 'Homem careca e endomesomórfico', peso: 7 }] }
];

function Coronariano({ setActiveTab, aluno, evaluationId, setEvaluationId, evaluationData, updateEvaluationData }) {
  const [etapa, setEtapa] = useState(1);
  const [showInfo, setShowInfo] = useState(false);
  const [isNextModal, setIsNextModal] = useState(false);
  
  const [respostas, setRespostas] = useState(evaluationData?.coronariano ||
    PERGUNTAS_CORONARIANO.map(p => ({ id: p.id, valor: "", peso: 0 }))
  );

  useEffect(() => {
    if (evaluationData?.coronariano) {
      setRespostas(evaluationData.coronariano);
    }
  }, [evaluationData]);
  
  const totalEtapas = 3;
  const porcentagemConclusao = (etapa / totalEtapas) * 100;
  const RADIAN = Math.PI / 180;

  const pesoTotal = respostas.reduce((acc, curr) => acc + (curr.peso || 0), 0);

  const obterNivelDeRisco = (pontos) => {
    if (pontos <= 11) return 'Risco bem abaixo da média';
    if (pontos <= 17) return 'Risco abaixo da média';
    if (pontos <= 24) return 'Risco na média geral';
    if (pontos <= 31) return 'Risco moderado';
    if (pontos <= 40) return 'Risco em nível perigoso';
    return 'Risco avançado';
  };

  const atualizarResposta = (perguntaId, opcao) => {
    setRespostas(prev => prev.map(item => 
      item.id === perguntaId ? { ...item, valor: opcao.label, peso: opcao.peso } : item
    ));
  };

  const needle = (value, cx, cy, iR, oR, color) => {
    const total = 60;
    let perc_ang = 0;
    if (value <= 11) perc_ang = (value / 11) * 10;
    else if (value <= 17) perc_ang = 10 + ((value - 11) / 6) * 10;
    else if (value <= 24) perc_ang = 20 + ((value - 18) / 6) * 10;
    else if (value <= 31) perc_ang = 30 + ((value - 25) / 6) * 10;
    else if (value <= 40) perc_ang = 40 + ((value - 32) / 8) * 10;
    else perc_ang = 50 + Math.min(((value - 41) / 19) * 10, 10);

    const ang = 180.0 * (1 - perc_ang / total);
    const length = (iR + 2 * oR) / 3;
    const sin = Math.sin(-RADIAN * ang);
    const cos = Math.cos(-RADIAN * ang);
    const r = 5;
    const x0 = cx;
    const y0 = cy;
    const xp = x0 + length * cos;
    const yp = y0 + length * sin;

    return [
      <circle key="c" cx={x0} cy={y0} r={r} fill={color} stroke="none" />,
      <path key="p" d={`M${x0 - r * sin} ${y0 + r * cos} L${x0 + r * sin} ${y0 - r * cos} L${xp} ${yp} Z`} fill={color} />
    ];
  };

  const handleCoronarianoRegister = async () => {
    const payload = {
      coronariano: respostas,
      evaluationId
    };

    try {
      const response = await api.post(`/api/student/${aluno.id}/evaluation/coronariano`, payload, { withCredentials: true });
      if (response.status === 200) {
        if (response.data.evaluationId && setEvaluationId) {
          if (!evaluationId) {
            if (!aluno.avaliacoesRealizadas) aluno.avaliacoesRealizadas = [];
            aluno.avaliacoesRealizadas.push({
              id: response.data.evaluationId,
              data: new Date().toISOString()
            });
          }
          setEvaluationId(response.data.evaluationId);
        }
        if (updateEvaluationData) {
          updateEvaluationData({ coronariano: respostas });
        }
        setIsNextModal(true);
      }
    } catch (error) {
      console.error('Erro ao salvar Coronariano:', error);
    }
  };

  const renderPergunta = (perguntaId) => {
    const pergunta = PERGUNTAS_CORONARIANO.find(p => p.id === perguntaId);
    const rObj = respostas.find(item => item.id === perguntaId);
    const valorAtual = rObj ? rObj.valor : "";
    const opcoesDaPergunta = OPCOES_CORONARIANO.find(opt => opt.id === perguntaId)?.opcoes || [];

    return (
      <div className="question-item" key={perguntaId}>
        <div className="standard-title">{pergunta.texto}</div>
        <div className="answer-container">
          {opcoesDaPergunta.map((opcao, i) => (
            <label className="answer-item" key={i}>
              <input
                type="radio"
                className="answer-radio"
                name={`coronariano_${perguntaId}`}
                checked={valorAtual === opcao.label}
                onChange={() => atualizarResposta(perguntaId, opcao)}
              />
              <p className="answer-title">{opcao.label}</p>
            </label>
          ))}
        </div>
      </div>
    );
  };

  const dataGrafico = [
    { value: 10, color: 'rgb(0, 197, 10)' }, { value: 10, color: 'rgb(98, 201, 2)' },
    { value: 10, color: 'rgb(238, 222, 0)' }, { value: 10, color: 'rgb(255, 174, 0)' },
    { value: 10, color: 'rgb(235, 106, 0)' }, { value: 10, color: 'rgb(206, 0, 0)' },
  ];

  return (
    <div className="avaliacao-container">
      <div className="avaliacao-content">
        {etapa === 1 && (
          <div className="questions-container">
            <div className="questions-left">{[0, 1].map(id => renderPergunta(id))}</div>
            <div className="questions-right">{[2, 3].map(id => renderPergunta(id))}</div>
          </div>
        )}

        {etapa === 2 && (
          <div className="questions-container">
            <div className="questions-left">{[4, 5].map(id => renderPergunta(id))}</div>
            <div className="questions-right">{[6, 7].map(id => renderPergunta(id))}</div>
          </div>
        )}

        {etapa === 3 && (
          <div className="questions-container">
            <div className="questions-left">
              <div className="question-title coronariano-title">Resultados do teste</div>
              <div className="graph-container">
                <PieChart width={270} height={150}>
                  <Pie dataKey="value" startAngle={180} endAngle={0} data={dataGrafico} cx={135} cy={120} innerRadius={50} outerRadius={100} stroke="none">
                    {dataGrafico.map((entry, index) => <Cell key={index} fill={entry.color} />)}
                  </Pie>
                  {needle(pesoTotal, 135, 120, 50, 100, '#e5e1cb')}
                </PieChart>
              </div>
              <div className='indice-coronariano'>
                <strong>{obterNivelDeRisco(pesoTotal)}</strong><br/>
                Resultado: {pesoTotal} pontos
              </div>
              <div className='interpretacao-coronariano'>
                <p className={pesoTotal <= 11 ? "risko-1 bold" : ""}>06-11 pontos: Risco bem abaixo da média</p>
                <p className={pesoTotal >= 12 && pesoTotal <= 17 ? "risko-2 bold" : ""}>12-17 pontos: Risco abaixo da média</p>
                <p className={pesoTotal >= 18 && pesoTotal <= 24 ? "risko-3 bold" : ""}>18-24 pontos: Risco na média geral</p>
                <p className={pesoTotal >= 25 && pesoTotal <= 31 ? "risko-4 bold" : ""}>25-31 pontos: Risco moderado</p>
                <p className={pesoTotal >= 32 && pesoTotal <= 40 ? "risko-5 bold" : ""}>32-40 pontos: Risco em nível perigoso</p>
                <p className={pesoTotal > 40 ? "risko-6 bold" : ""}>41-60 pontos: Risco avançado</p>
              </div>
            </div>
          </div>
        )}

        <Modal isOpen={isNextModal} onRequestClose={() => setIsNextModal(false)} className="modal-next" overlayClassName="popup-overlay">
          <p className="popupNext-h1">Risco Coronariano finalizado!</p>
          <p className="popupNext-h2">Deseja ir para a Antropometria?</p>
          <div className="sub-pagination-buttons">
            <button className="next-prev-button" onClick={() => setActiveTab(aluno?.profile === "idosos" ? "AntropometriaIdosos" : "Antropometria")}>Sim</button>
            <button className="next-prev-button" onClick={() => setIsNextModal(false)}>Não</button>
          </div>
        </Modal>

        <div className="pagination-footer">
          <div className="sub-pagination-footer">
            <div className="sub-pagination-buttons">
              <button className="next-prev-button" disabled={etapa === 1} onClick={() => setEtapa(etapa - 1)}>Voltar</button>
              {etapa < 3 ? (
                <button className="next-prev-button" onClick={() => setEtapa(etapa + 1)}>Avançar</button>
              ) : (
                <button className="next-prev-button" onClick={handleCoronarianoRegister}>Registrar</button>
              )}
            </div>
            <div className="progress-container">
              <div className="progress-bar" style={{ width: `${porcentagemConclusao}%` }}></div>
            </div>
          </div>
          <div className="info-box-container" onMouseEnter={() => setShowInfo(true)} onMouseLeave={() => setShowInfo(false)}>
            <div className="info-button">
              <HelpCircle size={20} /> Mais informações
            </div>
            {showInfo && (
              <div className="info-box-content">
                <p>O teste de risco coronariano é uma ferramenta de triagem que ajuda a identificar, de forma simples e rápida, a probabilidade de desenvolver doenças cardiovasculares com base em fatores como idade, histórico familiar, tabagismo, pressão arterial, colesterol, diabetes, peso corporal, alimentação, nível de atividade física e estresse.
                  <br/><br/>
                  Por meio de uma pontuação atribuída a cada resposta, o teste oferece uma estimativa do seu nível de risco cardíaco atual, funcionando como um importante instrumento de conscientização e prevenção.
                  <br/><br/>
                  Seu principal objetivo não é diagnosticar doenças, mas alertar sobre hábitos e condições que podem aumentar o risco de problemas como hipertensão, infarto e outras doenças do coração. Quanto maior a pontuação, maior a necessidade de atenção e mudanças no estilo de vida.
                  <br/><br/>
                  Utilize este resultado como um guia para adotar hábitos mais saudáveis, melhorar sua rotina e buscar acompanhamento profissional quando necessário. Prevenir é sempre o melhor caminho para proteger sua saúde cardiovascular.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Coronariano;